import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext.jsx";
import { API_BASE } from "../../api.js";
import { fixImageUrl } from "../../utils/fixImageUrl.js";
import "./Dashboard.css";

const EDITABLE = [
  { name: "name",      key: "dashboard.fullName",   type: "text" },
  { name: "specialty", key: "dashboard.specialty",   type: "text" },
  { name: "country",   key: "dashboard.country",     type: "text" },
  { name: "address",   key: "dashboard.address",     type: "text" },
  { name: "telephone", key: "dashboard.phone",       type: "tel"  },
  { name: "website",   key: "dashboard.website",     type: "url"  },
  { name: "instagram", key: "dashboard.instagram",   type: "text" },
  { name: "facebook",  key: "dashboard.facebook",    type: "text" },
];

function StatCard({ value, label, icon }) {
  return (
    <div className="db-stat">
      <span className="db-stat-icon">{icon}</span>
      <span className="db-stat-value">{value}</span>
      <span className="db-stat-label">{label}</span>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { artist: authArtist, token, logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const avatarInput = useRef(null);

  const [credForm, setCredForm] = useState({ username: "", newPassword: "", confirmPassword: "" });
  const [credSaving, setCredSaving] = useState(false);
  const [credMsg, setCredMsg] = useState("");
  const [credErr, setCredErr] = useState("");

  useEffect(() => {
    if (!token || !authArtist?._id) {
      navigate("/login");
      return;
    }
    axios
      .get(`${API_BASE}/getRally/${authArtist._id}`)
      .then((r) => {
        setProfile(r.data);
        setForm(r.data);
      })
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [authArtist, token, navigate]);

  const handleFormChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveMsg("");
    setSaveErr("");
    try {
      const fd = new FormData();
      const FIELDS = ["name","specialty","country","address","telephone",
                      "website","instagram","facebook","intro","biography"];
      for (const key of FIELDS) {
        if (form[key] !== undefined) fd.append(key, form[key]);
      }
      if (avatarFile) fd.append("profileImage", avatarFile);

      const res = await axios.put(
        `${API_BASE}/updateRally/${authArtist._id}`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      setProfile(res.data);
      setForm(res.data);
      setAvatarFile(null);
      setAvatarPreview(null);
      setSaveMsg(t("dashboard.saved"));
      setEditing(false);
    } catch (err) {
      setSaveErr(t("dashboard.saveErr"));
    } finally {
      setSaving(false);
    }
  };

  const handleCredentials = async (e) => {
    e.preventDefault();
    setCredErr(""); setCredMsg("");
    if (credForm.newPassword && credForm.newPassword !== credForm.confirmPassword) {
      setCredErr(t("dashboard.credErrMatch")); return;
    }
    if (!credForm.username && !credForm.newPassword) {
      setCredErr(t("dashboard.credErrEmpty")); return;
    }
    setCredSaving(true);
    try {
      await axios.put(`${API_BASE}/api/me/credentials`,
        { username: credForm.username || undefined, newPassword: credForm.newPassword || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCredMsg(t("dashboard.credOk"));
      setCredForm({ username: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setCredErr(err.response?.data?.error || t("dashboard.credErrFail"));
    } finally {
      setCredSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/Home");
  };

  if (loading) {
    return (
      <div className="db-loading">
        <div className="db-spinner" />
        <span>{t("dashboard.loading")}</span>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="db-loading">
        <p>{t("dashboard.loadErr")}</p>
        <a href="/Home" className="spar-btn spar-btn-primary" style={{ marginTop: 16 }}>{t("dashboard.goHome")}</a>
      </div>
    );
  }

  const avatarSrc = avatarPreview || fixImageUrl(profile.profileImage);

  return (
    <div className="db-page">

      {/* ── Sidebar ── */}
      <aside className="db-sidebar">

        <div className="db-avatar-wrap">
          <img
            src={avatarSrc || "/placeholder.jpg"}
            alt={profile.name}
            className="db-avatar"
            onError={(e) => { e.target.style.background = "var(--red)"; e.target.src = ""; }}
          />
          {editing && (
            <>
              <button
                className="db-avatar-btn"
                type="button"
                onClick={() => avatarInput.current?.click()}
                title="Change photo"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </button>
              <input
                ref={avatarInput}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleAvatarChange}
              />
            </>
          )}
        </div>

        <h2 className="db-sidebar-name">{profile.name}</h2>
        <p className="db-sidebar-specialty">{profile.specialty}</p>
        {profile.country && (
          <span className="db-sidebar-country">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {profile.country}
          </span>
        )}

        <nav className="db-sidebar-nav">
          <a href={`/ArtistProfile/${profile._id}`} className="db-nav-link" target="_blank" rel="noopener noreferrer">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            {t("dashboard.viewProfile")}
          </a>
          <button className="db-nav-link" onClick={() => { setEditing(!editing); setSaveMsg(""); setSaveErr(""); }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            {editing ? t("dashboard.cancelEdit") : t("dashboard.editProfile")}
          </button>
          <a href="/Chat" className="db-nav-link">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            {t("dashboard.community")}
          </a>
          <button className="db-nav-link db-nav-logout" onClick={handleLogout}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            {t("dashboard.signOut")}
          </button>
        </nav>
      </aside>

      {/* ── Main ── */}
      <div className="db-main">

        <div className="db-stats-row">
          <StatCard value={profile.images?.length ?? 0}  label={t("dashboard.artworks")}  icon="🎨" />
          <StatCard value={profile.country || "—"}        label={t("dashboard.location")}  icon="📍" />
          <StatCard value={profile.specialty}             label={t("dashboard.specialty")} icon="✦"  />
          <StatCard value={profile.email}                 label={t("dashboard.email")}     icon="✉"  />
        </div>

        {saveMsg && <div className="spar-alert spar-alert-success" style={{ marginBottom: 20 }}>{saveMsg}</div>}
        {saveErr && <div className="spar-alert spar-alert-error" style={{ marginBottom: 20 }}>{saveErr}</div>}

        {editing ? (
          <form className="db-edit-form" onSubmit={handleSave}>
            <h3 className="db-section-title">{t("dashboard.editTitle")}</h3>

            <div className="db-form-grid">
              {EDITABLE.map((f) => (
                <div className="spar-form-group" key={f.name}>
                  <label className="spar-label">{t(f.key)}</label>
                  <input
                    className="spar-input"
                    type={f.type}
                    name={f.name}
                    value={form[f.name] || ""}
                    onChange={handleFormChange}
                  />
                </div>
              ))}
            </div>

            <div className="spar-form-group">
              <label className="spar-label">{t("dashboard.shortIntro")}</label>
              <textarea
                className="spar-textarea"
                name="intro"
                rows={3}
                value={form.intro || ""}
                onChange={handleFormChange}
              />
            </div>

            <div className="spar-form-group">
              <label className="spar-label">{t("dashboard.fullBio")}</label>
              <textarea
                className="spar-textarea"
                name="biography"
                rows={6}
                value={form.biography || ""}
                onChange={handleFormChange}
              />
            </div>

            <div className="db-form-actions">
              <button type="submit" className="spar-btn spar-btn-primary" disabled={saving}>
                {saving ? <><span className="spar-spinner" /> {t("dashboard.saving")}</> : t("dashboard.save")}
              </button>
              <button type="button" className="spar-btn spar-btn-ghost" onClick={() => { setEditing(false); setForm(profile); setAvatarPreview(null); setAvatarFile(null); }}>
                {t("dashboard.cancel")}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="db-info-section">
              <h3 className="db-section-title">{t("dashboard.about")}</h3>
              {profile.intro && <p className="db-info-intro">{profile.intro}</p>}
              {profile.biography && <p className="db-info-bio">{profile.biography}</p>}
            </div>

            <div className="db-info-section">
              <h3 className="db-section-title">{t("dashboard.contactLinks")}</h3>
              <div className="db-contact-grid">
                {profile.telephone && <div className="db-contact-item"><span>{t("dashboard.phone")}</span><span>{profile.telephone}</span></div>}
                {profile.email && <div className="db-contact-item"><span>{t("dashboard.email")}</span><span>{profile.email}</span></div>}
                {profile.address && <div className="db-contact-item"><span>{t("dashboard.address")}</span><span>{profile.address}</span></div>}
                {profile.website && <div className="db-contact-item"><span>{t("dashboard.website")}</span><a href={profile.website} target="_blank" rel="noopener noreferrer">{profile.website}</a></div>}
                {profile.instagram && <div className="db-contact-item"><span>{t("dashboard.instagram")}</span><a href={`https://instagram.com/${profile.instagram.replace("@","")}`} target="_blank" rel="noopener noreferrer">{profile.instagram}</a></div>}
                {profile.facebook && <div className="db-contact-item"><span>{t("dashboard.facebook")}</span><a href={profile.facebook} target="_blank" rel="noopener noreferrer">{profile.facebook}</a></div>}
              </div>
            </div>
          </>
        )}

        <div className="db-info-section">
          <h3 className="db-section-title">{t("dashboard.credentials")}</h3>
          {credMsg && <div className="spar-alert spar-alert-success" style={{ marginBottom: 12 }}>{credMsg}</div>}
          {credErr && <div className="spar-alert spar-alert-error" style={{ marginBottom: 12 }}>{credErr}</div>}
          <form onSubmit={handleCredentials}>
            <div className="db-form-grid">
              <div className="spar-form-group">
                <label className="spar-label">{t("dashboard.credUsername")}</label>
                <input className="spar-input" type="text" placeholder={profile.username || t("dashboard.credUsername")}
                  value={credForm.username} onChange={e => setCredForm(f => ({ ...f, username: e.target.value }))} />
              </div>
              <div className="spar-form-group">
                <label className="spar-label">{t("dashboard.credNewPw")}</label>
                <input className="spar-input" type="password" value={credForm.newPassword}
                  onChange={e => setCredForm(f => ({ ...f, newPassword: e.target.value }))} />
              </div>
              <div className="spar-form-group">
                <label className="spar-label">{t("dashboard.credConfirmPw")}</label>
                <input className="spar-input" type="password" value={credForm.confirmPassword}
                  onChange={e => setCredForm(f => ({ ...f, confirmPassword: e.target.value }))} />
              </div>
            </div>
            <button type="submit" className="spar-btn spar-btn-primary" disabled={credSaving} style={{ marginTop: 8 }}>
              {credSaving ? <><span className="spar-spinner" /> {t("dashboard.credSaving")}</> : t("dashboard.credSave")}
            </button>
          </form>
        </div>

        {profile.images?.length > 0 && (
          <div className="db-portfolio-section">
            <h3 className="db-section-title">{t("dashboard.portfolio")} ({profile.images.length})</h3>
            <div className="db-portfolio-grid">
              {profile.images.map((img, i) => (
                <div key={i} className="db-portfolio-item">
                  <img
                    src={fixImageUrl(img)}
                    alt={`Artwork ${i + 1}`}
                    className="db-portfolio-img"
                    onError={(e) => { e.target.parentElement.style.display = "none"; }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
